// Checks the value typed in the regular expression rule's box as this rule alone would judge a saved
// answer, and shows the result on the fields: the regular expression's validity on its field, the
// value's on the value field, each red with its message when it fails and green when it passes.
import { Controller } from "stimulus"
import { post } from "@rails/request.js"

// Milliseconds after the last edit.
const DELAY = 400
const CHECKED = "custom-validations__checked-field"
const PASSED = "custom-validations__field--passed"
const ICON = "custom-validations__field-icon"
const ICONS = { failed: "error", passed: "check_circle" }

export default class extends Controller {
  static targets = ["value"]

  // Listened to here, not through a data-action: the settings pages' nested-form script (Kegs.Form)
  // swallows clicks on any element that has one.
  connect() {
    this.schedule = this.schedule.bind(this)

    this.element.addEventListener("input", this.schedule)
    this.prepare(this.regularExpressionField)
    this.prepare(this.valueTarget)
  }

  disconnect() {
    this.element.removeEventListener("input", this.schedule)
    clearTimeout(this.timer)
  }

  // Clears the results an edit of the regular expression or value makes stale, so the next one visibly
  // arrives, and checks DELAY after the last edit. One check at a time: an edit while one is pending
  // only marks it stale.
  schedule(event) {
    if (![this.regularExpressionField, this.valueTarget].includes(event.target)) return

    if (event.target === this.regularExpressionField) this.mark(this.regularExpressionField)

    this.mark(this.valueTarget)
    clearTimeout(this.timer)

    if (this.pending) {
      this.stale = true
    } else {
      this.timer = setTimeout(() => this.check(), DELAY)
    }
  }

  // Sends the fields as they are now and shows their results, or, when edits made it stale, checks the
  // latest fields DELAY after it arrives instead. A blank regular expression is left unchecked.
  async check() {
    const fields = {
      regular_expression: this.regularExpressionField.value,
      value: this.valueTarget.value,
    }

    if (fields.regular_expression.trim() === "") return

    this.pending = true
    this.stale = false
    const results = await this.request(fields)
    this.pending = false

    if (this.stale) {
      this.timer = setTimeout(() => this.check(), DELAY)
    } else {
      this.show(results)
    }
  }

  // Each field's result as [state, message].
  async request(fields) {
    try {
      const response = await post(this.element.dataset.checksUrl, {
        body: { regular_expression_check: fields },
        responseKind: "json",
      })

      if (!response.ok && !response.unprocessableEntity) return this.failure

      const { errors } = await response.json

      return this.results(errors, fields.value)
    } catch {
      return this.failure
    }
  }

  // The regular expression fails with its error or passes. The value, only when typed under a valid
  // regular expression, fails with why (no match, or the try-again message) or passes.
  results(errors, value) {
    const [regularExpressionError] = errors.regular_expression || []
    const [valueError] = errors.value || []

    if (regularExpressionError) return { regularExpression: ["failed", regularExpressionError], value: [] }

    if (value.trim() === "") return { regularExpression: ["passed"], value: [] }

    return { regularExpression: ["passed"], value: valueError ? ["failed", valueError] : ["passed"] }
  }

  show({ regularExpression, value }) {
    if (regularExpression) this.mark(this.regularExpressionField, ...regularExpression)

    this.mark(this.valueTarget, ...value)
  }

  // Shows a field's result as a field shows a save error: red with an error icon and the message under
  // it when it failed, green with a check icon when it passed, plain otherwise.
  mark(field, state, message = "") {
    const textfield = field.closest(".mdl-textfield")

    textfield.classList.toggle("is-invalid", state === "failed")
    textfield.classList.toggle(PASSED, state === "passed")
    textfield.querySelector(`.${ICON}`).textContent = ICONS[state] || ""
    textfield.querySelector(".mdl-textfield__error").textContent = message
  }

  // Gives a field, once, an icon and an error to show its result in, keeping the error simple_form
  // renders after a failed save, with its icon. The error is announced when it changes.
  prepare(field) {
    const textfield = field.closest(".mdl-textfield")
    const error = textfield.querySelector(".mdl-textfield__error") || document.createElement("span")

    textfield.classList.add(CHECKED)
    error.classList.add("mdl-textfield__error")
    error.setAttribute("aria-live", "polite")
    textfield.append(error)

    if (textfield.querySelector(`.${ICON}`)) return

    const icon = document.createElement("i")

    icon.className = `material-icons ${ICON}`
    icon.setAttribute("aria-hidden", "true")
    icon.textContent = textfield.classList.contains("is-invalid") ? ICONS.failed : ""
    textfield.append(icon)
  }

  // When the request fails (403, 429, or no answer): the try-again message on the value field, and the
  // regular expression's field left as it is.
  get failure() {
    return { value: ["failed", this.element.dataset.failureLabel] }
  }

  get regularExpressionField() {
    return this.element.querySelector("input[name$='[regular_expression]']")
  }
}
